import * as React from "react";
import DatePicker from "react-datepicker";
import { getDatePickerLocaleProps } from "utils/dateLocales";
import "react-datepicker/dist/react-datepicker.css";
import "./DateTimeRangeSelector.scss";
import { toUTC } from "utils/dates";
import Button from "react-bootstrap/Button";

export interface ControlledDateRangeSelectorProps {
  startDate: Date;
  endDate: Date;
  onChange: (range: [Date, Date]) => void;
  quickChoices?: [string, number | Date][];
  disableMidnightRounding?: boolean;
  disabled?: boolean;
}

export const getDefaultDateRange = (): [Date, Date] => {
  const endDate = toUTC(new Date());
  const startDate = toUTC(new Date(+endDate - 7 * 86400000));
  endDate.setHours(0, 0, 0, 0);
  startDate.setHours(0, 0, 0, 0);
  return [startDate, endDate];
};

const ControlledDateRangeSelector = (
  props: ControlledDateRangeSelectorProps,
) => {
  const {
    startDate,
    endDate,
    onChange,
    quickChoices = [],
    disableMidnightRounding = false,
    disabled = false,
  } = props;

  const setStartDate = (value: Date | null) => {
    if (value) {
      onChange([value, endDate]);
    }
  };

  const setEndDate = (value: Date | null) => {
    if (value) {
      onChange([startDate, value]);
    }
  };

  const [quickSelectIndex, setQuickSelectIndex] = React.useState<
    number | undefined
  >();

  React.useEffect(() => {
    const getActiveQuickChoiceIndex = () => {
      if (!startDate || !endDate) {
        return undefined;
      }

      const microsecondsDiff = endDate.getTime() - startDate.getTime();

      let activeIndex: number | undefined;
      quickChoices.forEach((choice, index) => {
        if (choice[1] instanceof Date) {
          return;
        }
        const choiceDiff = Math.abs(microsecondsDiff - choice[1]);
        if (activeIndex === undefined && choiceDiff === 0) {
          activeIndex = index;
        }
      });

      return activeIndex;
    };

    setQuickSelectIndex(getActiveQuickChoiceIndex());
  }, [endDate, startDate, quickChoices]);

  return (
    <div className="DateTimeRangeSelector">
      <div className="date-inputs">
        <DatePicker
          selected={startDate}
          showTimeSelect
          onChange={(d) => {
            if (!d || !endDate) {
              return;
            }
            d = new Date(Math.min(+d, +endDate));
            setStartDate(d);
            setQuickSelectIndex(undefined);
          }}
          maxDate={endDate}
          filterTime={(d) => !endDate || d <= endDate}
          dateFormat="dd.MM.yyyy HH:mm"
          className="form-control my-1"
          {...getDatePickerLocaleProps()}
          disabled={disabled}
        />
        <DatePicker
          selected={endDate}
          showTimeSelect
          onChange={(d) => {
            if (!d || !startDate) {
              return;
            }
            d = new Date(Math.max(+d, +startDate));
            setEndDate(d);
            setQuickSelectIndex(undefined);
          }}
          minDate={startDate}
          filterTime={(d) => !startDate || d >= startDate}
          dateFormat="dd.MM.yyyy HH:mm"
          className="form-control my-1"
          {...getDatePickerLocaleProps()}
          disabled={disabled}
        />
      </div>
      {!!quickChoices.length && (
        <div className="quick-choices">
          {quickChoices.map(([text, quickChoice], index) => (
            <Button
              key={text}
              type="button"
              variant="outline-primary"
              size="sm"
              className="my-1 me-2"
              active={
                quickSelectIndex !== undefined && quickSelectIndex === index
              }
              onClick={(e) => {
                e.preventDefault();
                if (disabled) {
                  return;
                }
                const end = new Date();
                let start;
                if (quickChoice instanceof Date) {
                  start = quickChoice;
                } else {
                  start = new Date(+end - quickChoice);
                }
                if (
                  !disableMidnightRounding &&
                  !(quickChoice instanceof Date) &&
                  quickChoice >= 86400000
                ) {
                  end.setHours(0, 0, 0, 0);
                  start.setHours(0, 0, 0, 0);
                }
                onChange([start, end]);
                setQuickSelectIndex(index);
              }}
              disabled={disabled}
            >
              {text}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ControlledDateRangeSelector;
